import React from "react";
import { StyleSheet, View, TouchableOpacity, ScrollView } from "react-native";
import { router } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { useThemeColor } from "@/hooks/useThemeColor";
import { IconSymbol } from "@/components/ui/IconSymbol";

/**
 * Design Sandbox Dashboard Screen.
 * Lists all interactive component design harnesses available to admins and testers.
 */
export default function DesignIndexScreen() {
  const tintColor = useThemeColor({}, "tint");
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const textMuted = useThemeColor({}, "textMuted");

  return (
    <ThemedScreen
      title="Design Sandbox"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin" as any),
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 8 }}>
          UI / UX WORKBENCHES
        </ThemedText>

        <View style={styles.list}>
          <TouchableOpacity
            onPress={() => router.push("/admin/design/themed-editor" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="textformat.size" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      ThemedEditor (Normal Text & Formats)
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>New</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Intuitive WYSIWYG & Normal Text</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Intuitive editor: normal-text editing, zero-keyboard format application & removal via active format pills, media copy/paste with in-place sizing, and interactive tables with outer +Row/+Col buttons.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Normal Text</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Zero-Keyboard</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Table Controls</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Media Sizing</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push("/admin/design/design-style" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="textformat.size" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      Design Style Guide
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>New</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Design System & Tokens</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Interactive style showcase: tighter spacing scale, darker shading for read-only elements, compact button variants, typography hierarchy, and color tokens.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Read-Only Shading</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Compact Controls</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Color Tokens</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push("/admin/design/symbols" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="star.fill" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      Platform Symbols
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>New</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Status Indicators & Semantic Affordances</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Canonical status indicators: Owner (gold key), 5-Star rating (gold star), Saved (cloud with check), Shared with you, Physical Heirloom, Read-Only, and Offline Synced.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Gold Key Owner</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>5-Star Gold Star</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Saved Cloud</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Item Cards</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push("/admin/design/sharing-control" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="share.fill" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      Sharing Control
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>Interactive</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Memory Sharing</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Evaluate the unified Sharing Control with 5 contacts (2 shared from others) and 5 groups (2 shared from others). Test adding shares, revoking shares, and owner context attribution.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>5 Contacts</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>5 Groups</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Owner Context</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push("/admin/design/themed-list-and-cards" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="list.bullet" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      Themed List & Card Workbench
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>New</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Lists, Filtering & Sorting</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Generic card list container featuring top-left live text filtering, top-right attribute sorting, card hover feedback, and multi-entity switching across Memories, Contacts, Groups, Media, and Shares.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Live Filter</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Attribute Sort</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Hover Indicator</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>5 Card Types</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push("/admin/design/animated-header" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="chevron.left.forwardslash.chevron.right" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      Animated Header & Scrolling
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>Workbench</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Navigation & Header Elevation</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Inspect dynamic scroll offset calculations, header background opacity transitions, collapsible sections, and dense list inertial scrolling.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Scroll Transition</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Collapsible Views</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Header Elevation</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push("/admin/design/memory-date" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="calendar" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      Memory Date Component
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>New</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Temporal Representation & Date Ranges</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Design sign-off workbench for MemoryDate: supports exact dates/times, approximate dates, year/month, year-only, and date ranges (e.g. wedding weekend, holiday spans) with JSON unpackable serialization.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Date Ranges</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Approximate Dates</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>JSON Storage</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Sign-off Ready</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push("/admin/design/date-parser" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="calendar" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      Date Parser & Range Tester
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>New</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Date Parsing, JSON Standardization & Permutations</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Design tester for DateParser: tests free-text parsing into canonical JSON date ranges matching DatePicker, missing year filling (2026), 2-digit year century expansion, approximate ~ indicators, and multi-day / multi-month "to" ranges.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Year Fill (2026)</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>2-Digit Years</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Approximate (~)</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>"to" Ranges</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>JSON Storage</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push("/admin/design/memory-filter" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="line.3.horizontal.decrease.circle" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      MemoryFilter & Export
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>New</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Multidimensional Entity Filtering & Export</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Standardized Archival Heirloom Memory Filter: filter across Date To/From, Contact associations, Group memberships, Media types (image/video/audio), and minimum ratings, with one-click Archival PDF and Image export triggers.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Date Ranges</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Contacts & Groups</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Ratings (1-5★)</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>PDF & Image Export</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push("/admin/design/media-filter" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="photo.on.rectangle.angled" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      MediaFilter
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>New</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Multi-Dimensional Media & Heirloom Asset Filtering</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Multi-dimensional Heirloom Media Filter: filter by media categories, types, date ranges, ratings (1-5★), memory associations, contacts, groups, and tags.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Media Types</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Categories</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Memory & People</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Ratings & Tags</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push("/admin/design/visualiser" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="circle.grid.cross" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      Visualiser (D3 Graph)
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>New</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Relational D3 Force Simulation</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Relational canvas using D3 force simulation: memory block dimension proportional to duration (days), media dot radius scaled by rating (1-5★), memory distance scaled by time delta, with pan/zoom and SVG/PDF export.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>D3 Force</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Logarithmic Sizing</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Pan & Zoom</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Archival Vector SVG</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push("/admin/design/selectors" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="tray.2.fill" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      Selectors & Selection Modals
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>New</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Contextual Selection Popovers & Entity Selectors</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Interactive testing laboratory for all platform selection controls: ContactSelector, GroupSelector, MemorySelector, MediaSelector, TagSelector, DateSelector, GoogleMapsLocationSelector, ShareSelector, and ShareRecipientSelector.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>ContactSelector</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>GroupSelector</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>MemorySelector</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>MediaSelector</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>TagSelector</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>DateSelector</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>GoogleMapsLocationSelector</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>ShareSelector</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>ShareRecipientSelector</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push("/admin/design/add-to-memory" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="plus.rectangle.on.rectangle" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      AddToMemory / MemorySelector Dialog
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>New</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Media Archival & Memory Association</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Dedicated evaluation harness for MemorySelector (Add to Memory mode): test media assignment across variable memory counts (empty, standard 3, dense 12+), single vs multi-media batches, persistent scrollbar behavior, and instant creation flows.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Modal Dialog</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Multi-Media Batch</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Persistent Scrollbar</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Empty State</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push("/admin/design/action-hover-panel" as any)}
            activeOpacity={0.7}
          >
            <ThemedPanel padding={10} elevation="low">
              <View style={styles.cardHeader}>
                <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
                  <IconSymbol name="layers.fill" size={24} color={tintColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                      Action Hover Panel
                    </ThemedText>
                    <View style={[styles.statusBadge, { backgroundColor: badgeBg }]}>
                      <ThemedText style={[styles.statusText, { color: tintColor }]}>New</ThemedText>
                    </View>
                  </View>
                  <ThemedText style={styles.cardDomain}>Domain: Contextual Batch Selection Toolbar</ThemedText>
                </View>
              </View>

              <ThemedText style={styles.cardDescription}>
                Archival Blue Linen floating contextual action toolbar for multi-item selection across Media, Memories, Contacts, Groups, and Shares. Features count badge, Select All, dismiss, and action tooltips.
              </ThemedText>

              <View style={styles.tagRow}>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Floating Toolbar</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Batch Actions</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Archival Blue Linen</ThemedText>
                </View>
                <View style={[styles.tag, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.tagText}>Tooltips</ThemedText>
                </View>
              </View>
            </ThemedPanel>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    paddingBottom: 24,
  },
  list: {
    gap: 8,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 10,
  },
  iconContainer: {
    width: 44,
    height: 44,
    borderRadius: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardTitle: {
    fontSize: 17,
  },
  cardDomain: {
    fontSize: 12,
    opacity: 0.6,
    marginTop: 2,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 0,
  },
  statusText: {
    fontSize: 11,
    fontWeight: "700",
  },
  cardDescription: {
    fontSize: 13,
    opacity: 0.8,
    lineHeight: 18,
    marginBottom: 12,
  },
  tagRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 0,
  },
  tagText: {
    fontSize: 11,
    fontWeight: "600",
    opacity: 0.7,
  },
});
